import {
  PaginatedUserPreferences,
  useListUserPreferences,
  useUpdateUserPreference,
} from "@/services/orval/internalApiV1";

export function useUserPreferences() {
  const {
    data,
    refetch: refetchUserPrefs,
    isLoading,
  } = useListUserPreferences();
  const queryClient = useQueryClient();
  const { mutateAsync } = useUpdateUserPreference({
    mutation: {
      onSuccess() {
        refetchUserPrefs();
      },
      async onMutate(variables) {
        // Optimistic update logic taken from: https://tanstack.com/query/v5/docs/framework/vue/guides/optimistic-updates#updating-a-single-todo
        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({ queryKey: ["user_preferences"] });

        // Snapshot the previous value, necessary for rollback on error
        const previousUserPreference =
          queryClient.getQueryData<PaginatedUserPreferences>([
            "user_preferences",
          ]);

        const newUserPreference = JSON.parse(
          JSON.stringify(previousUserPreference)
        ) as PaginatedUserPreferences; // Create a deep copy of the previousUserPreference

        // Update the newUserPreference with the incoming variables
        const changedElement = newUserPreference.collection.find((pref) => {
          return pref.key === variables.data.preference.key;
        });
        if (changedElement) {
          // Update the existing preference
          changedElement.value = variables.data.preference.value;
          changedElement.updatedAt = new Date().toISOString();
        } else {
          // Preference does not exist, add it
          newUserPreference.collection.push({
            key: variables.data.preference.key,
            value: variables.data.preference.value,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }
        // Optimistically update to the new value
        queryClient.setQueryData(["user_preferences"], newUserPreference);
        return { previousUserPreference };
      },
      onError: (_err, _new, context) => {
        queryClient.setQueryData(
          ["user_preferences"],
          context?.previousUserPreference
        );
      },
    },
  });

  const userPreference = (key: string) => {
    return computed(() => {
      if (data.value) {
        const preference = data.value.collection.find(
          (pref) => pref.key === key
        );
        if (preference) {
          return preference.value;
        }
        return null;
      }
      return undefined; // Data is not yet loaded
    });
  };

  const updateUserPreference = async (key: string, value: string) => {
    await mutateAsync({ data: { preference: { key, value } } }, {});
  };

  return {
    isLoading,
    userPreference,
    updateUserPreference,
  };
}
